import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SERVER_BASE_URL } from '../../utils/api';

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 60px 20px;
`;
const Title = styled.h1`
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 24px;
  color: #444;
`;
const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 18px;
`;
const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;
const TableWrapper = styled.div`
  background: #fff;
  border-radius: 10px 10px 0 0;
  overflow-x: auto;
  margin-bottom: 24px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.05rem;
`;
const Th = styled.th`
  background: #f7f7f7;
  font-weight: 700;
  padding: 10px 8px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  white-space: nowrap;
`;
const Td = styled.td`
  padding: 8px 8px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
  white-space: nowrap;
`;
const ChartBlock = styled.div`
  background: #fff;
  border-radius: 0 0 10px 10px;
  padding: 24px 24px 12px 24px;
`;
const Message = styled.div`
  padding: 24px;
  background: #fff;
  border-radius: 10px;
  color: #666;
`;

const formatPoints = (value) => (Number(value) || 0).toLocaleString('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

const RewardsReport = () => {
  const [monthsCount, setMonthsCount] = useState(12);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${SERVER_BASE_URL}/api/marketing/rewards-report?months=${monthsCount}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || 'Не удалось загрузить отчёт');
        }
        const data = await response.json();
        setReport(data);
      } catch (e) {
        setError(e.message);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [monthsCount]);

  const months = report?.months || [];
  const rows = report?.rows || [];
  const totalsByMonth = report?.totalsByMonth || [];
  const chartData = months.map((m, i) => ({
    month: m.label,
    total: totalsByMonth[i] || 0
  }));
  const grandTotal = totalsByMonth.reduce((sum, v) => sum + (Number(v) || 0), 0);

  return (
    <>
      <DashboardHeader />
      <Wrapper>
        <Title>Отчёт: мои вознаграждения</Title>
        <FiltersRow>
          <Select value={monthsCount} onChange={(e) => setMonthsCount(parseInt(e.target.value, 10))}>
            <option value={6}>6 месяцев</option>
            <option value={12}>12 месяцев</option>
            <option value={24}>24 месяца</option>
          </Select>
        </FiltersRow>

        {loading && <Message>Загрузка...</Message>}
        {error && <Message style={{ color: '#b91c1c' }}>{error}</Message>}

        {!loading && !error && report && (
          <>
            {grandTotal === 0 ? (
              <Message>За выбранный период начислений нет.</Message>
            ) : (
              <>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Бонус</Th>
                        {months.map((m) => <Th key={`${m.year}-${m.month}`}>{m.label}</Th>)}
                        <Th>Итого</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.filter((row) => row.total > 0).map((row) => (
                        <tr key={row.key}>
                          <Td>{row.label}</Td>
                          {row.byMonth.map((val, idx) => (
                            <Td key={`${row.key}-${idx}`}>{formatPoints(val)}</Td>
                          ))}
                          <Td><b>{formatPoints(row.total)}</b></Td>
                        </tr>
                      ))}
                      <tr>
                        <Td><b>Итого</b></Td>
                        {totalsByMonth.map((val, idx) => (
                          <Td key={`total-${idx}`}><b>{formatPoints(val)}</b></Td>
                        ))}
                        <Td><b>{formatPoints(grandTotal)}</b></Td>
                      </tr>
                    </tbody>
                  </Table>
                </TableWrapper>
                <ChartBlock>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <XAxis dataKey="month" tick={{ fontSize: 13 }} angle={-20} textAnchor="end" height={60} interval={0} />
                      <YAxis />
                      <Tooltip formatter={(v) => formatPoints(v)} />
                      <Legend />
                      <Bar dataKey="total" name="Итого баллов" fill="#4fc3f7" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartBlock>
              </>
            )}
          </>
        )}
      </Wrapper>
      <DashboardFooter />
    </>
  );
};

export default RewardsReport;
